import React, { useEffect, useState, useRef } from 'react';
import ConfirmModal from './components/ConfirmModal';
import { useLocation } from 'react-router-dom';
import { useScoreboard } from './context/ScoreboardContext';
import { useDashboardLanguage } from './context/DashboardLanguageContext';
import { useLanguage } from './context/LanguageContext';

const GameTimer = () => {
  // Safe check for router context
  let isLiveView = false;
  try {
    const location = useLocation();
    isLiveView = location.pathname === '/liveview';
  } catch (error) {
    // If useLocation fails, check window location as fallback
    isLiveView = window.location.pathname === '/liveview';
  }
  const { t } = useDashboardLanguage();
  // The period-change dialog is an OPERATOR dialog, so it follows the control-panel language, not the
  // audience display language — the same `t` the reset dialog in Scoreboard.jsx uses.
  const { t: ut } = useLanguage();
  const {
    calculateCurrentTime,
    period,
    isGameRunning,
    startStopClock,
    setClockTime,
    setClockPeriod,
    backgroundColor,
    defaultGameDuration,
    defaultOvertime,
    formatTime,
    calculateElapsedTime
  } = useScoreboard();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isEditingPeriod, setIsEditingPeriod] = useState(false);
  // Modal state for period change confirmations
  const [showPeriodIncModal, setShowPeriodIncModal] = useState(false);
  const [showPeriodDecModal, setShowPeriodDecModal] = useState(false);
  const [pendingPeriodChange, setPendingPeriodChange] = useState(null);

  const setDefaultTimeForPeriod = (periodNum) => {
    if (periodNum < 1 || periodNum > 4) return; // Don't allow periods outside 1-4
    
    const [minutes, seconds] = (periodNum <= 2 ? defaultGameDuration : defaultOvertime).split(':').map(Number);
    const timeInSecs = minutes * 60 + seconds;
    setClockTime(timeInSecs);
  };

  const handleTimeClick = () => {
    if (!isGameRunning && !isLiveView) {
      setIsEditing(true);
      const currentTime = calculateCurrentTime();
      setEditValue(formatTime(currentTime));
    }
  };

  const handleTimeChange = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{0,2}:\d{0,2}$/) || value.match(/^\d{0,2}$/) || value === '') {
      setEditValue(value);
    }
  };

  const handleTimeBlur = () => {
    if (editValue === '') {
      setIsEditing(false);
      return;
    }

    let minutes = 0;
    let seconds = 0;

    if (editValue.includes(':')) {
      [minutes, seconds] = editValue.split(':').map(Number);
    } else {
      minutes = parseInt(editValue);
      seconds = 0;
    }

    if (!isNaN(minutes) && !isNaN(seconds) && minutes >= 0 && seconds >= 0 && seconds < 60) {
      setClockTime(minutes * 60 + seconds);
    }
    setIsEditing(false);
  };

  const handleTimeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      const currentTime = calculateCurrentTime();
      setEditValue(formatTime(currentTime));
    }
  };

  const toggleTimer = () => {
    startStopClock();
    if (isEditing) {
      setIsEditing(false);
    }
  };

  const handlePeriodChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= 4) {
      setClockPeriod(value);
      setDefaultTimeForPeriod(value);
    }
  };

  const handlePeriodIncrement = () => {
    if (period < 4) {
      setShowPeriodIncModal(true);
    }
  };

  const confirmPeriodIncrement = () => {
    setShowPeriodIncModal(false);
    setPendingPeriodChange(period + 1);
  };

  const handlePeriodDecrement = () => {
    if (period > 1) {
      setShowPeriodDecModal(true);
    }
  };

  const confirmPeriodDecrement = () => {
    setShowPeriodDecModal(false);
    setPendingPeriodChange(period - 1);
  };

  const handlePeriodBlur = () => {
    setIsEditingPeriod(false);
  };

  const handleKeyDown = (e) => {
    // Only react to 's' key when not in an input field and not in live view
    if (!isLiveView && e.key === 's' && e.target.tagName !== 'INPUT') {
      e.preventDefault();
      startStopClock();
      if (isEditing) {
        setIsEditing(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isGameRunning, isEditing, isLiveView]);  

  useEffect(() => {
    if (pendingPeriodChange !== null) {
      setClockPeriod(pendingPeriodChange);
      setDefaultTimeForPeriod(pendingPeriodChange);
      setPendingPeriodChange(null);
    }
  }, [pendingPeriodChange, setClockPeriod, setDefaultTimeForPeriod]);

  // Dynamic font sizing for live view
  const timerRef = useRef(null);
  
  useEffect(() => {
    if (!isLiveView) return;
    
    const calculateOptimalFontSize = () => {
      const container = timerRef.current;
      if (!container) return;
      
      // Get container dimensions
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      
      if (containerWidth === 0 || containerHeight === 0) {
        setTimeout(calculateOptimalFontSize, 50);
        return;
      }
      
      // Calculate available width (subtract padding)
      const computedStyle = window.getComputedStyle(container);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      
      // Reduce available width to create more left/right padding
      const extraPadding = containerWidth * 0.05; // 10% of container width as extra padding
      const availableWidth = containerWidth - paddingLeft - paddingRight - extraPadding;
      
      // Create a temporary element to measure text width
      const measureElement = document.createElement('span');
      measureElement.style.fontFamily = 'Chivo Mono, monospace';
      measureElement.style.fontWeight = 'bold';
      measureElement.style.visibility = 'hidden';
      measureElement.style.position = 'absolute';
      measureElement.style.whiteSpace = 'nowrap';
      measureElement.textContent = '00:00';
      document.body.appendChild(measureElement);
      
      // Binary search for optimal font size
      let minSize = 12;
      let maxSize = 300;
      let optimalSize = minSize;
      
      while (minSize <= maxSize) {
        const testSize = Math.floor((minSize + maxSize) / 2);
        measureElement.style.fontSize = `${testSize}px`;
        
        const textWidth = measureElement.offsetWidth;
        
        if (textWidth <= availableWidth) {
          optimalSize = testSize;
          minSize = testSize + 1;
        } else {
          maxSize = testSize - 1;
        }
      }
      
      // Clean up
      document.body.removeChild(measureElement);
      
      //console.log('Font calc:', { availableWidth, optimalSize });
      container.style.fontSize = `${optimalSize}px`;
    };
    
    const timeoutId = setTimeout(calculateOptimalFontSize, 100);
    window.addEventListener('resize', calculateOptimalFontSize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateOptimalFontSize);
    };
  }, [isLiveView]);

  if (isLiveView) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full" style={{ backgroundColor }}>  
        <div 
          ref={timerRef}
          className="font-chivo font-bold text-white py-0.5 text-center w-full mx-auto px-2 game-timer-responsive" 
          style={{
            lineHeight: '1',
            wordBreak: 'keep-all',
            whiteSpace: 'nowrap'
          }}
        >
          {formatTime(calculateCurrentTime())}
        </div>

        <div className="flex flex-col items-center justify-center py-2">
          <div className="text-white text-[5vh] font-bold text-center">
            {t('period.label')}
          </div>
        </div>
        <div className="p-[1vh] w-[24vh] h-[8vh] flex items-center justify-center">
          <span className="text-[9vh] font-bold font-chivo text-white">
            {period}
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center justify-start h-full px-[2vh] pt-[2vh] mx-auto" style={{ backgroundColor }}>
        <button
          onClick={toggleTimer}
          className="mb-[2vh] px-[2vh] py-[2vh] bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-600 w-[26vh] text-[4vh]"
          title="Hint: Press 's' to start/stop the game."
        >
          {isGameRunning ? 'STOP' : 'START'}
        </button>
        
        <div 
          className={`text-[6vh] font-bold bg-white rounded-lg border border-gray-300 p-[1vh] text-center w-[26vh] ${!isGameRunning && !isLiveView ? 'cursor-pointer' : ''}`}
          onClick={handleTimeClick}
        >
          {isEditing ? (
            <input
              type="text"
              value={editValue}
              onChange={handleTimeChange}
              onBlur={handleTimeBlur}
              onKeyDown={handleTimeKeyDown}
              className="w-full h-full text-[6vh] font-bold text-center bg-transparent outline-none"
              autoFocus
            />
          ) : (
            formatTime(calculateCurrentTime())
          )}
        </div>

        <div 
          className="text-[2vh] font-bold bg-white rounded-lg border border-gray-300 p-[1vh] text-center w-[26vh] mt-[1vh]"
        >
          {formatTime(calculateElapsedTime())}
        </div>

        <div className="flex flex-col items-center justify-center px-12 py-4">
          <div className="text-white text-[4vh] font-bold text-center">
            {t('period.label')}
          </div>
        </div>

        <div className="flex items-center gap-[2vh]">      
          <button
            onClick={handlePeriodDecrement}
            className="w-[8vh] h-[8vh] bg-amber-500 text-black text-[4vh] font-bold rounded-lg hover:bg-amber-600"
            disabled={isGameRunning || period <= 1}
          >
            -
          </button>
          {isEditingPeriod ? (
            <input
              type="number"
              value={period}
              onChange={handlePeriodChange}
              onBlur={handlePeriodBlur}
              min="1"
              max="4"
              className="bg-white rounded-lg border border-gray-300 p-[1vh] w-[12vh] text-[6vh] font-bold text-center appearance:textfield [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              autoFocus
            />
          ) : (
            <div
              onClick={() => !isGameRunning && setIsEditingPeriod(true)}
              className="bg-white rounded-lg border border-gray-300 p-[1vh] w-[12vh] text-center cursor-pointer"
            >
              <span className={`${isLiveView ? 'text-[6vh]' : 'text-[4vh]' } font-bold `}>
                {period}
              </span>
            </div>
          )}
          <button
            onClick={handlePeriodIncrement}
            className="w-[8vh] h-[8vh] bg-amber-500 text-black text-[4vh] font-bold rounded-lg hover:bg-amber-600"
            disabled={isGameRunning || period >= 4}
          >
            +
          </button>
        </div>
      </div>
      <ConfirmModal
        open={showPeriodIncModal}
        title={ut('confirmModal.changePeriod')}
        message={ut('confirmModal.changePeriodMessage').replace('{period}', period + 1)}
        onConfirm={confirmPeriodIncrement}
        onCancel={() => setShowPeriodIncModal(false)}
        confirmLabel={ut('confirmModal.change')}
        cancelLabel={ut('confirmModal.cancel')}
      />
      <ConfirmModal
        open={showPeriodDecModal}
        title={ut('confirmModal.changePeriod')}
        message={ut('confirmModal.changePeriodMessage').replace('{period}', period - 1)}
        onConfirm={confirmPeriodDecrement}
        onCancel={() => setShowPeriodDecModal(false)}
        confirmLabel={ut('confirmModal.change')}
        cancelLabel={ut('confirmModal.cancel')}
      />
    </>
  );
};


export default GameTimer;